import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { env } from '@/lib/env'
import { useAuthStore } from '@/store/authStore'
import type { ApiErrorResponse } from '@/types/api'

export class ApiError extends Error {
  readonly status: number | null
  readonly fieldErrors: Record<string, string[]> | null

  constructor(
    message: string,
    status: number | null,
    fieldErrors: Record<string, string[]> | null,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.fieldErrors = fieldErrors
  }
}

/**
 * Sanctum SPA auth: withCredentials sends/receives the session + XSRF-TOKEN
 * cookies, and axios's built-in withXSRFToken mirrors that cookie into the
 * X-XSRF-TOKEN header on state-changing requests automatically.
 */
export const apiClient = axios.create({
  baseURL: `${env.apiBaseUrl}/api/v1`,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
  },
})

/**
 * Laravel Sanctum's SPA cookie flow requires this endpoint to be hit once
 * before the first state-changing request in a session (login, register).
 * Safe to call repeatedly — it just (re)issues the XSRF-TOKEN cookie.
 */
export async function ensureCsrfCookie(): Promise<void> {
  await axios.get(`${env.apiBaseUrl}/sanctum/csrf-cookie`, { withCredentials: true })
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => config)

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    const status = error.response?.status ?? null

    if (status === 401) {
      useAuthStore.getState().clearSession()
    }

    const payload = error.response?.data
    const message = payload?.message ?? error.message ?? 'Something went wrong. Please try again.'
    const fieldErrors = payload?.errors ?? null

    return Promise.reject(new ApiError(message, status, fieldErrors))
  },
)
